import type { DeliveryMethodKind } from "@dransay/api/models";

export function isMapDeliveryMethod(
	deliveryMethodKind?: DeliveryMethodKind,
): boolean {
	return deliveryMethodKind === "pickup" || deliveryMethodKind === "courier";
}

export const formatDistanceFromMeters = (distanceMeters: number): string => {
	if (distanceMeters < 1000) return `~${Math.round(distanceMeters)}m`;
	const km = distanceMeters / 1000;
	if (km < 10) return `~${(Math.round(km * 10) / 10).toString()}km`;
	return `~${Math.round(km)}km`;
};

export const formatDistanceFromKm = (distanceKm: number): string => {
	if (distanceKm < 1) return `${Math.round(distanceKm * 1000)}m`;
	if (distanceKm < 10) return `${Math.round(distanceKm * 10) / 10}km`;
	return `${Math.round(distanceKm)}km`;
};

export const formatPickerDistanceLabel = ({
	distanceMeters,
	distanceKm,
	locale,
}: {
	distanceMeters?: number | null;
	distanceKm?: number | null;
	locale: string;
}): string | undefined => {
	if (distanceMeters != null && Number.isFinite(distanceMeters)) {
		return formatDistanceFromMeters(distanceMeters);
	}
	if (distanceKm != null && Number.isFinite(distanceKm)) {
		return `~${formatDistanceFromKm(distanceKm)}`;
	}
	return undefined;
};

export const nonTopPharmacyToggleWrapperClassName = "pt-1";

export const nonTopPharmacyToggleButtonClassName =
	"w-fit text-body-bold py-3 text-ansay-dark cursor-pointer";

export const pharmacyPickerScrollBottomFadeClassName =
	"pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[34px] opacity-80 bg-gradient-to-t from-white to-transparent";

/**
 * Radix ScrollArea scrollbar shared by the picker dropdown and the PDP dialog
 * (Figma 4597:58936). A native `overflow-y-auto` thumb auto-hides behind
 * macOS overlay scrollbars, so both surfaces render this one instead.
 */
export const pharmacyPickerScrollbarClassName =
	"flex select-none touch-none p-0.5 bg-gray-100 transition-colors duration-150 ease-out hover:bg-gray-200 data-[orientation=vertical]:w-2";

export const pharmacyPickerScrollbarThumbClassName =
	"relative flex-1 rounded-[10px] bg-gray-300 before:absolute before:top-1/2 before:left-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']";

export function isScrollableElement(element: Element): boolean {
	const { overflow, overflowY } = getComputedStyle(element);
	return /auto|scroll|overlay/.test(`${overflow}${overflowY}`);
}

function isOverflowingScrollContainer(element: HTMLElement): boolean {
	return element.scrollHeight > element.clientHeight;
}

function findRadixScrollRoot(sentinel: HTMLElement): HTMLElement | null {
	const radixViewport = sentinel.closest("[data-radix-scroll-area-viewport]");
	if (!(radixViewport instanceof HTMLElement)) return null;
	const outerScroller = radixViewport.parentElement;
	if (
		outerScroller instanceof HTMLElement &&
		isScrollableElement(outerScroller) &&
		isOverflowingScrollContainer(outerScroller)
	) {
		return outerScroller;
	}
	// Always root the observer on the picker's own viewport — even when it is
	// not (yet) overflowing. Otherwise short result lists fall back to an outer
	// scrollable ancestor, against which the sentinel reads as "not intersecting"
	// and the sticky banner latches to its pinned (white) state while it is not
	// actually stuck.
	return radixViewport;
}

export function findScrollRoot(sentinel: HTMLElement): Element | null {
	const radixScrollRoot = findRadixScrollRoot(sentinel);
	if (radixScrollRoot) return radixScrollRoot;
	const scrollables: HTMLElement[] = [];
	let node: HTMLElement | null = sentinel.parentElement;
	while (node) {
		if (isScrollableElement(node)) scrollables.push(node);
		if (node.getAttribute("role") === "dialog") break;
		node = node.parentElement;
	}
	// A sticky element sticks within its NEAREST scroll container, so root the
	// observer there — regardless of whether it currently overflows. Picking the
	// first *overflowing* ancestor instead can skip the real scroll parent (e.g.
	// a not-yet-overflowing full-screen overlay) for a farther one like <html>,
	// against which the sentinel never reads as "stuck".
	return scrollables[0] ?? null;
}
